import {
  ComponentFactoryResolver,
  ComponentRef,
  Injectable,
  Injector,
  OnDestroy,
  Type,
} from '@angular/core';

import { getPresetMetadataFrom, PresetMetadata } from './metadata/preset';
import {
  PRESET_COMPS_TOKEN,
  PRESET_TYPES_TOKEN,
  PresetType,
} from './preset-token';
import { EMPTY_VAL, hasTypeAsProto, isEmptyVal } from './utils';

interface TypeWithPresetMetadata<T = any> {
  type: Type<T>;
  metadata: PresetMetadata[];
}
export class PresetService implements OnDestroy {
private presetCompRefs: ComponentRef<any>[] = [];
private finalPresetComp: any;
private presetsInit = false;
/**
 * @return {?}
 */
private get presetTypes() {
    return this.injector.get(PRESET_TYPES_TOKEN);
  }
/**
 * @return {?}
 */
private get components() {
    return this.injector
      .get(PRESET_COMPS_TOKEN)
      .reduce((comps, compsInner) => [...comps, ...compsInner], []);
  }
/**
 * @template T
 * @param {?} type
 * @return {?}
 */
private static resolvePresetTypeMetadata<T>(
    type: Type<T>,
  ): TypeWithPresetMetadata<T> {
    return {
      type,
      metadata: getPresetMetadataFrom(type),
    };
  }
/**
 * @template T
 * @param {?} data
 * @return {?}
 */
private static isPresetMetadataExist<T>(
    data: TypeWithPresetMetadata<T>,
  ): boolean {
    return !!data.metadata.length;
  }
/**
 * @param {?} injector
 * @param {?} compResolver
 */
constructor(
private injector: Injector,
private compResolver: ComponentFactoryResolver,
  ) {}
/**
 * @template T
 * @return {?}
 */
getPreset<T = any>(): T {
    if (this.presetTypes.length && !this.presetCompRefs.length) {
      this.presetCompRefs = this.presetTypes
        .filter(hasTypeAsProto.bind(null, PresetType))
        .map(presetType => this.createCompFromType(presetType));
    }

    if (!this.finalPresetComp) {
      this.finalPresetComp = this.mergeCompRefs(this.presetCompRefs);
    }

    return this.finalPresetComp;
  }
/**
 * \@internal
 * @return {?}
 */
ngOnDestroy() {
    this.presetCompRefs.forEach(presetCompRef => presetCompRef.destroy());
    this.presetCompRefs = [];
  }
/**
 * \@internal
 * @return {?}
 */
initDecoratedPresets() {
    if (this.presetsInit) {
      return;
    }

    this.components
      .map(PresetService.resolvePresetTypeMetadata)
      .filter(PresetService.isPresetMetadataExist)
      .forEach(type => this.initPresetsOn(type));

    this.presetsInit = true;
  }
/**
 * @param {?} type
 * @return {?}
 */
private createCompFromType(type: PresetType) {
    return this.compResolver
      .resolveComponentFactory( /** @type {?} */((type as any)))
      .create(this.injector);
  }
/**
 * @template T
 * @param {?} compRefs
 * @return {?}
 */
private mergeCompRefs<T>(compRefs: ComponentRef<T>[]): T {
    const /** @type {?} */ proto = compRefs.reduce(
      (comp, compRef) =>
        Object.assign(comp, compRef.instance.constructor.prototype),
      {},
    );

    return compRefs.reduce(
      (comp, compRef) => Object.assign(comp, compRef.instance), /** @type {?} */((
      proto as T)),
    );
  }
/**
 * @param {?} __0
 * @return {?}
 */
private initPresetsOn({ type, metadata }: TypeWithPresetMetadata) {
    metadata.forEach(data => this.initPresetOn(type, data));
  }
/**
 * @param {?} type
 * @param {?} metadata
 * @return {?}
 */
private initPresetOn(type: Type<any>, metadata: PresetMetadata) {
    let /** @type {?} */ value: any = EMPTY_VAL;
    const /** @type {?} */ get = () => (isEmptyVal(value) ? (value = this.getPreset()) : value);
    const /** @type {?} */ set = (val: any) => (value = val);

    if (delete type.prototype[metadata.propName]) {
      Object.defineProperty(type.prototype, metadata.propName, {
        get,
        set,
        configurable: true,
        enumerable: true,
      });
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Injector, },
{type: ComponentFactoryResolver, },
];
}

function PresetService_tsickle_Closure_declarations() {
/** @type {?} */
PresetService.decorators;
/**
 * @nocollapse
 * @type {?}
 */
PresetService.ctorParameters;
/** @type {?} */
PresetService.prototype.presetCompRefs;
/** @type {?} */
PresetService.prototype.finalPresetComp;
/** @type {?} */
PresetService.prototype.presetsInit;
/** @type {?} */
PresetService.prototype.injector;
/** @type {?} */
PresetService.prototype.compResolver;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
